import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import classNames from "./SettingsDialog.module.css";
import {
  useMediaDeviceSelect,
  usePersistentUserChoices,
} from "@livekit/components-react";
import TextSpeed from "../settings/TextSpeed";
import TextCustomization from "../settings/TextCustomization";
import { LocalAudioTrack, LocalVideoTrack, getBrowser } from "livekit-client";
import { useRenderLanguage } from "../../../hooks/user";
import { VoiceOptions } from "./voice/VoiceOptionsDialog";
import VideoBackgrounds from "../../routes/meeting/VideoBackgrounds";
import RadixDialog from "./RadixDialog";
import { useRoom } from "../../context/room/room";
import Loader from "../Loader";
import { LocalStorageKeys } from "../../../types";
import useLayout from "../../../hooks/layout";
import { useAudioDeviceId, useVideoDeviceId } from "../../../hooks/media";
import Toggle from "../Toggle";
import Select from "../Select";
import { useIsMobile } from "../../../hooks/layout";

function AudioSettings(props: { localAudioTrack?: LocalAudioTrack }) {
  const { devices, setActiveMediaDevice } = useMediaDeviceSelect({
    kind: "audioinput",
    track: props.localAudioTrack,
  });
  // Initialize device settings
  const { saveAudioInputDeviceId } = usePersistentUserChoices();
  const [audioDeviceId, setAudioDeviceId] = useAudioDeviceId();
  useEffect(() => {
    saveAudioInputDeviceId(audioDeviceId);
  }, [audioDeviceId, saveAudioInputDeviceId]);

  return (
    <div className={classNames.SettingDetails}>
      <div className={classNames.SettingLabel}>
        {"Audio Input (Microphone)"}
      </div>
      <Select
        triggerClassName={classNames.SelectTrigger}
        selected={audioDeviceId}
        onChange={(id) => {
          setActiveMediaDevice(id as string);
          setAudioDeviceId(id as string);
        }}
        items={devices.map((d) => ({ value: d.deviceId, label: d.label }))}
        defaultLabel="Default"
      />
    </div>
  );
}

function VideoSettings(props: { localVideoTrack?: LocalVideoTrack }) {
  const { devices, setActiveMediaDevice } = useMediaDeviceSelect({
    kind: "videoinput",
    track: props.localVideoTrack,
  });
  // Initialize device settings
  const { saveVideoInputDeviceId } = usePersistentUserChoices();
  const [videoDeviceId, setVideoDeviceId] = useVideoDeviceId();
  useEffect(() => {
    saveVideoInputDeviceId(videoDeviceId);
  }, [videoDeviceId, saveVideoInputDeviceId]);

  const browser = useMemo(() => getBrowser(), []);
  const isMobile = useIsMobile();

  return (
    <div className={classNames.SettingDetails}>
      <div className={classNames.SettingLabel}>{"Video Input (Camera)"}</div>
      <Select
        triggerClassName={classNames.SelectTrigger}
        selected={videoDeviceId}
        onChange={(id) => {
          setActiveMediaDevice(id as string);
          setVideoDeviceId(id as string);
        }}
        items={devices.map((d) => ({ value: d.deviceId, label: d.label }))}
        defaultLabel="Default"
      />
      {browser?.name === "Chrome" && !isMobile && <VideoBackgrounds />}
    </div>
  );
}

const settingCategories = [
  "audio",
  "video",
  "voice-avatar",
  "text-customization",
  "text-speed",
  "text-translation",
] as const;

type SettingCategory = (typeof settingCategories)[number];

export type SettingsDialogProps = {
  trigger: ReactNode | ReactNode[];
  hiddenCategories?: SettingCategory[];
  localAudioTrack?: LocalAudioTrack;
  localVideoTrack?: LocalVideoTrack;
  teams?: boolean;
  initialSelectedSetting?: SettingCategory;
};

export function Settings(props: Omit<SettingsDialogProps, "trigger">) {
  const { room, organization, loading } = useRoom();

  // Filter out hidden categories
  const visibleCategories = useMemo(() => {
    if (!props.hiddenCategories) {
      return settingCategories;
    }
    const hiddenCategories = [...props.hiddenCategories];
    if (!organization?.voice_translation_enabled) {
      hiddenCategories.push("voice-avatar");
    }
    return settingCategories.filter(
      (category) => !hiddenCategories.includes(category)
    );
  }, [props.hiddenCategories, organization]);

  // Save to local storage on change
  const [layout, setLayout] = useLayout();
  useEffect(() => {
    localStorage.setItem(
      LocalStorageKeys.LAYOUT_SETTINGS,
      JSON.stringify(layout.settings)
    );
  }, [layout.settings]);

  const getCategoryName = useCallback((category: SettingCategory) => {
    switch (category) {
      case "audio":
        return "Audio";
      case "video":
        return "Video";
      case "voice-avatar":
        return "Voice avatar";
      case "text-customization":
        return "Text customization";
      case "text-speed":
        return "Text speed";
      case "text-translation":
        return "Text translation";
    }
  }, []);

  const [selectedSetting, setSelectedSetting] = useState<SettingCategory>(
    props.initialSelectedSetting ?? visibleCategories[0]
  );

  // Update selected setting is no longer in visible settings
  useEffect(() => {
    if (!visibleCategories.includes(selectedSetting)) {
      setSelectedSetting(visibleCategories[0]);
    }
  }, [visibleCategories, selectedSetting]);

  const renderLanguage = useRenderLanguage();
  const renderCategory = useMemo(() => {
    switch (selectedSetting) {
      case "audio":
        return <AudioSettings localAudioTrack={props.localAudioTrack} />;
      case "video":
        return <VideoSettings localVideoTrack={props.localVideoTrack} />;
      case "voice-avatar":
        return (
          <div className={classNames.SettingDetails}>
            <VoiceOptions
              selectedVoiceId={room?.default_voice_avatar}
              basic={!organization?.voice_avatar_enabled}
              locale={renderLanguage || "en-US"}
            />
          </div>
        );
      case "text-customization":
        return (
          <div className={classNames.SettingDetails}>
            <TextCustomization />
          </div>
        );
      case "text-speed":
        return (
          <div className={classNames.SettingDetails}>
            <TextSpeed />
          </div>
        );
      case "text-translation":
        return (
          <div className={classNames.SettingDetails}>
            <label>Enable text translation</label>
            <Toggle
              enabled={layout.settings.textTranslationEnabled}
              onChange={(value) =>
                setLayout((prevState) => ({
                  ...prevState,
                  settings: {
                    ...prevState.settings,
                    textTranslationEnabled: value,
                  },
                }))
              }
            />
          </div>
        );
    }
  }, [
    selectedSetting,
    props.localAudioTrack,
    props.localVideoTrack,
    renderLanguage,
    room?.default_voice_avatar,
    organization?.voice_avatar_enabled,
    layout.settings.textTranslationEnabled,
    setLayout,
  ]);

  if (!room || !organization || loading) {
    return <Loader />;
  }

  return (
    <div
      className={`${classNames.SettingsDialog} ${
        props.teams ? classNames.TeamsSettingsDialog : ""
      }`}
    >
      <div className={classNames.RenderedSetting}>
        <div className={classNames.SettingsCategories}>
          {visibleCategories.map((category) => (
            <div
              key={category}
              aria-selected={selectedSetting === category}
              onClick={() => setSelectedSetting(category)}
            >
              {getCategoryName(category)}
            </div>
          ))}
        </div>
        {renderCategory}
      </div>
    </div>
  );
}

export default function SettingsDialog(props: SettingsDialogProps) {
  return (
    <RadixDialog
      title={"Settings"}
      trigger={props.trigger}
      className={classNames.SettingsDialog}
      content={<Settings {...props} />}
    />
  );
}
